import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, Percent, TicketPercent, BadgePercent, CalendarRange, Cake } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const KIND_META = {
  coupon: { label: "Coupon code", cls: "bg-[#144F75]/10 text-[#144F75]", icon: TicketPercent },
  membership: { label: "Membership", cls: "bg-[#5C8A72]/15 text-[#5C8A72]", icon: BadgePercent },
  promotion: { label: "Promotion", cls: "bg-[#D98E73]/15 text-[#B5794E]", icon: CalendarRange },
};
const STATUS_CLS = {
  active: "bg-[#5C8A72]/15 text-[#5C8A72]", scheduled: "bg-[#144F75]/10 text-[#144F75]",
  expired: "bg-[#F1EDE6] text-[#8FA89B]", disabled: "bg-[#F1EDE6] text-[#8FA89B]",
  exhausted: "bg-[#E0A458]/20 text-[#B5794E]",
};
const APPLIES_LABEL = { all: "Everything", services: "Services", events: "Events & classes", merch: "Merchandise" };
const TIER_LABEL = { discovery: "Discovery", community: "Community", enhancer: "Enhancer" };

function BirthdayPerkCard() {
  const qc = useQueryClient();
  const locQ = useQuery({ queryKey: ["mgr-my-location"], queryFn: () => api.get("/manager/me/location").then((r) => r.data) });
  const svcQ = useQuery({ queryKey: ["mgr-my-services"], queryFn: () => api.get("/manager/me/services").then((r) => r.data) });
  const [perk, setPerk] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (locQ.data && perk === null) {
      setPerk({ enabled: false, kind: "percent", value: 15, service_id: "", note: "", ...(locQ.data.birthday_perk || {}) });
    }
  }, [locQ.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!perk) return null;

  const save = async () => {
    if (perk.enabled && perk.kind !== "free_service" && !(Number(perk.value) > 0)) { toast.error("Set a discount value."); return; }
    if (perk.enabled && perk.kind === "free_service" && !perk.service_id) { toast.error("Pick the free service."); return; }
    setSaving(true);
    try {
      const svc = (svcQ.data || []).find((s) => s.id === perk.service_id);
      await api.put("/manager/me/location", { birthday_perk: { ...perk, value: Number(perk.value) || 0, service_title: svc?.title || "" } });
      toast.success(perk.enabled ? "Birthday perk saved — it applies automatically during each client's birthday week." : "Birthday perk turned off.");
      qc.invalidateQueries({ queryKey: ["mgr-my-location"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-5 mb-5" data-testid="mgr-birthday-perk">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Cake size={18} className="text-[#D98E73]" /> Birthday perk</h3>
        <label className="flex items-center gap-2 text-sm text-[#4A5D6B] cursor-pointer">
          <input type="checkbox" checked={!!perk.enabled} onChange={(e) => setPerk({ ...perk, enabled: e.target.checked })} data-testid="mgr-birthday-enabled" />
          Enabled
        </label>
      </div>
      <p className="text-xs text-[#8FA89B] mt-1 mb-3">Clients with a birthday on file get greeted at the front desk during their birthday week (±3 days), and this treat applies automatically at checkout — once per client per year.</p>
      {perk.enabled && (
        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          <select value={perk.kind} onChange={(e) => setPerk({ ...perk, kind: e.target.value })} data-testid="mgr-birthday-kind" className="eyw-input text-sm">
            <option value="percent">% off the sale</option>
            <option value="amount">$ off the sale</option>
            <option value="free_service">Free offering</option>
          </select>
          {perk.kind === "free_service" ? (
            <select value={perk.service_id} onChange={(e) => setPerk({ ...perk, service_id: e.target.value })} data-testid="mgr-birthday-service" className="eyw-input text-sm sm:col-span-2">
              <option value="">— Pick the free service —</option>
              {(svcQ.data || []).map((s) => <option key={s.id} value={s.id}>{s.title} ({money(Number(s.price))})</option>)}
            </select>
          ) : (
            <input type="number" min="1" value={perk.value} onChange={(e) => setPerk({ ...perk, value: e.target.value })} data-testid="mgr-birthday-value"
              placeholder={perk.kind === "percent" ? "% off" : "$ off"} className="eyw-input text-sm" />
          )}
          {perk.kind !== "free_service" && (
            <input value={perk.note} onChange={(e) => setPerk({ ...perk, note: e.target.value })} data-testid="mgr-birthday-note" placeholder="Front-desk note (optional)" className="eyw-input text-sm" />
          )}
        </div>
      )}
      <button onClick={save} disabled={saving} data-testid="mgr-birthday-save" className="eyw-btn !py-1.5 !px-4 text-xs disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={13} /> : null} Save birthday perk
      </button>
    </div>
  );
}

export default function CouponsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-coupons"], queryFn: () => api.get("/manager/coupons").then((r) => r.data) });
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["mgr-coupons"] });

  const toggle = async (c) => {
    try {
      await api.put(`/manager/coupons/${c.id}/active`, { active: c.status === "disabled" });
      toast.success(c.status === "disabled" ? "Discount enabled" : "Discount disabled");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    try {
      await api.delete(`/manager/coupons/${delTarget.id}`);
      toast.success("Discount deleted");
      setDelTarget(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const all = data || [];
  const rows = filter === "all" ? all : all.filter((c) => c.status === filter);

  return (
    <div data-testid="mgr-coupons-panel">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Percent className="text-[#144F75]" size={22} /> Coupons & Discounts</h2>
        <button onClick={() => setModal({})} data-testid="mgr-add-coupon" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New discount</button>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">Coupon codes, automatic membership discounts and time-limited promotions for your center. Discounts never take a checkout below $10.</p>

      <BirthdayPerkCard />

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="mgr-coupon-filters">
        {["all", "active", "scheduled", "expired", "disabled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`mgr-coupon-filter-${f}`}
            className={`rounded-full px-3.5 py-1.5 text-xs capitalize border ${filter === f ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
            {f}{f !== "all" ? ` (${all.filter((c) => c.status === f).length})` : ` (${all.length})`}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#4A5D6B] eyw-card p-6 text-center" data-testid="mgr-coupons-empty">No {filter === "all" ? "" : filter + " "}discounts yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const meta = KIND_META[c.kind] || KIND_META.coupon;
            const Icon = meta.icon;
            return (
              <div key={c.id} className="eyw-card p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`mgr-coupon-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A242B] flex items-center gap-2 flex-wrap">
                    {c.name}
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.cls}`}><Icon size={10} /> {meta.label}</span>
                    <span data-testid={`mgr-coupon-status-${c.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || STATUS_CLS.expired}`}>{c.status}</span>
                  </p>
                  <p className="text-xs text-[#8FA89B] mt-1">
                    {c.kind === "coupon" && c.code && <span className="font-mono font-semibold text-[#144F75] mr-2">{c.code}</span>}
                    {c.discount_type === "percent" ? `${Number(c.value)}% off` : `${money(Number(c.value))} off`}
                    {" · "}{APPLIES_LABEL[c.applies_to] || c.applies_to}
                    {c.kind === "membership" && c.membership_tier ? ` · ${TIER_LABEL[c.membership_tier]} members` : ""}
                    {(c.starts_at || c.expires_at) && ` · ${c.starts_at || "now"} → ${c.expires_at || "no end"}`}
                    {c.kind === "coupon" && ` · used ${c.uses_count}${c.max_uses ? `/${c.max_uses}` : ""}×`}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => toggle(c)} data-testid={`mgr-coupon-toggle-${c.id}`}
                    className={`rounded-full px-3 py-1.5 text-xs border ${c.status === "disabled" ? "border-[#5C8A72] text-[#5C8A72] hover:bg-[#5C8A72]/10" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#8FA89B]"}`}>
                    {c.status === "disabled" ? "Enable" : "Disable"}
                  </button>
                  <button onClick={() => setModal(c)} data-testid={`mgr-coupon-edit-${c.id}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"><Pencil size={15} /></button>
                  <button onClick={() => setDelTarget(c)} data-testid={`mgr-coupon-delete-${c.id}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && <CouponModal coupon={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {delTarget && (
        <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={() => setDelTarget(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="mgr-coupon-delete-confirm">
            <h3 className="font-serif text-xl text-[#1A242B] mb-2">Delete “{delTarget.name}”?</h3>
            <p className="text-sm text-[#4A5D6B] mb-5">It will stop applying immediately. Past redemptions stay on record.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDelTarget(null)} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
              <button onClick={del} data-testid="mgr-coupon-delete-yes" className="eyw-btn !py-2 text-sm !bg-red-600 hover:!bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CouponModal({ coupon, onClose, onSaved }) {
  const svcQ = useQuery({ queryKey: ["mgr-my-services"], queryFn: () => api.get("/manager/me/services").then((r) => r.data) });
  const [form, setForm] = useState({
    name: coupon?.name || "", kind: coupon?.kind || "coupon", code: coupon?.code || "",
    discount_type: coupon?.discount_type || "percent", value: coupon?.value ?? 10,
    applies_to: coupon?.applies_to || "all", service_ids: coupon?.service_ids || [],
    membership_tier: coupon?.membership_tier || "community",
    starts_at: coupon?.starts_at || "", expires_at: coupon?.expires_at || "",
    max_uses: coupon?.max_uses ?? "", max_uses_per_client: coupon?.max_uses_per_client ?? "",
    active: coupon?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Give this discount a name");
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        max_uses_per_client: form.max_uses_per_client === "" ? null : Number(form.max_uses_per_client),
        membership_tier: form.kind === "membership" ? form.membership_tier : "",
        code: form.kind === "coupon" ? form.code : "",
      };
      if (coupon) await api.put(`/manager/coupons/${coupon.id}`, payload);
      else await api.post("/manager/coupons", payload);
      toast.success("Discount saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggleSvc = (id) => set("service_ids", form.service_ids.includes(id) ? form.service_ids.filter((x) => x !== id) : [...form.service_ids, id]);

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mgr-coupon-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">{coupon ? "Edit discount" : "New discount"}</h3>
        <div className="space-y-3">
          <div><label className="text-sm font-medium mb-1 block">Name</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="mgr-coupon-name" placeholder="e.g. Spring Reset, Gold Perk" className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Type</label>
            <select value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="mgr-coupon-kind" className="eyw-input w-full">
              <option value="coupon">Coupon code — client enters a code at checkout</option>
              <option value="membership">Membership discount — applies automatically by tier</option>
              <option value="promotion">Promotion — applies automatically during a date range</option>
            </select></div>
          {form.kind === "coupon" && (
            <div><label className="text-sm font-medium mb-1 block">Code</label>
              <input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="mgr-coupon-code" placeholder="e.g. WELCOME15" className="eyw-input w-full uppercase font-mono" /></div>
          )}
          {form.kind === "membership" && (
            <div><label className="text-sm font-medium mb-1 block">Membership tier</label>
              <select value={form.membership_tier} onChange={(e) => set("membership_tier", e.target.value)} data-testid="mgr-coupon-tier" className="eyw-input w-full">
                <option value="discovery">Discovery members</option>
                <option value="community">Community members</option>
                <option value="enhancer">Enhancers</option>
              </select></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Discount</label>
              <select value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="mgr-coupon-disctype" className="eyw-input w-full">
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed amount ($)</option>
              </select></div>
            <div><label className="text-sm font-medium mb-1 block">Value</label>
              <input type="number" min="0" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="mgr-coupon-value" className="eyw-input w-full" /></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Applies to</label>
            <select value={form.applies_to} onChange={(e) => set("applies_to", e.target.value)} data-testid="mgr-coupon-applies" className="eyw-input w-full">
              <option value="all">Everything at my center</option>
              <option value="services">Services only</option>
              <option value="events">Events & classes only</option>
              <option value="merch">Merchandise only</option>
            </select></div>
          {(form.applies_to === "services" || form.applies_to === "all") && form.kind !== "membership" && (svcQ.data || []).length > 0 && form.applies_to === "services" && (
            <div>
              <label className="text-sm font-medium mb-1 block">Limit to specific services <span className="text-[11px] text-[#8FA89B] font-normal">(none selected = all services)</span></label>
              <div className="max-h-36 overflow-y-auto rounded-xl border border-[#E5E0D8] p-2 space-y-1" data-testid="mgr-coupon-services">
                {(svcQ.data || []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
                    <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleSvc(s.id)} className="accent-[#144F75]" /> {s.title}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Starts <span className="text-[11px] text-[#8FA89B] font-normal">(optional)</span></label>
              <input type="date" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="mgr-coupon-starts" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Expires <span className="text-[11px] text-[#8FA89B] font-normal">(optional)</span></label>
              <input type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="mgr-coupon-expires" className="eyw-input w-full" /></div>
          </div>
          {form.kind === "coupon" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Max total uses <span className="text-[11px] text-[#8FA89B] font-normal">(blank = unlimited)</span></label>
                <input type="number" min="1" value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} data-testid="mgr-coupon-maxuses" className="eyw-input w-full" /></div>
              <div><label className="text-sm font-medium mb-1 block">Max per client</label>
                <input type="number" min="1" value={form.max_uses_per_client} onChange={(e) => set("max_uses_per_client", e.target.value)} data-testid="mgr-coupon-maxclient" className="eyw-input w-full" /></div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="mgr-coupon-active" /> Enabled</label>
          <p className="text-[11px] text-[#8FA89B]">No discount ever takes a checkout total below $10 — the system reduces the discount automatically and tells the client why.</p>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mgr-coupon-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}
